import React, { useMemo, useEffect, useState } from 'react';
import { XCircle, Music } from 'lucide-react';
import { SelectedDancerCardProps } from '../../types/dancers';
import { Participant } from '../../types/registration';

/**
 * Card component for displaying a selected dancer with workshop level selection
 */
const SelectedDancerCard: React.FC<SelectedDancerCardProps> = ({
  dancer,
  findAgeGroupInfo,
  workshopLevels,
  formatAgeRange,
  calculateAge,
  getInitials,
  getSuggestedWorkshopLevel,
  formatCurrency,
  getWorkshopLevel,
  updateDancerOptions,
  toggleDancerSelection,
  isDarkMode
}) => {
  const age = calculateAge(dancer.dateOfBirth || dancer.birthDate || '');
  const initialLevel = getWorkshopLevel(dancer);
  
  // Add local state to immediately update UI when selection changes
  const [localSelectedLevel, setLocalSelectedLevel] = useState<string>(initialLevel);
  
  // Update local state when dancer prop changes
  useEffect(() => {
    setLocalSelectedLevel(getWorkshopLevel(dancer));
  }, [dancer, getWorkshopLevel]);
  
  // Use localSelectedLevel for UI display
  const selectedLevel = localSelectedLevel;
  const hasLevel = !!selectedLevel;

  // Check if dancer is from a routine
  const isFromRoutine: boolean = 
    (!!dancer.isFromRoutine) || 
    (!!dancer.fromRoutine) || 
    (!!dancer.autoAdded) || 
    false;

  // Determine routines information
  const routines = (() => {
    // Handle custom routineNames property if it exists
    const customRoutineNames = (dancer as any).routineNames;
    if (Array.isArray(customRoutineNames) && customRoutineNames.length > 0) {
      return customRoutineNames;
    }
    
    // Handle custom fromRoutines property if it exists
    const customFromRoutines = (dancer as any).fromRoutines;
    if (Array.isArray(customFromRoutines) && customFromRoutines.length > 0) {
      return customFromRoutines;
    }
    
    // Standard properties from the Participant type
    if (dancer.routineName || dancer.fromRoutine) {
      return [dancer.routineName || dancer.fromRoutine];
    }
    
    return [] as string[];
  })();

  const hasMultipleRoutines = routines.length > 1;
  const primaryRoutine = routines.length > 0 ? routines[0] : "routine";

  // Get price for selected level
  const level = workshopLevels.find(
    (lvl) => lvl.id === selectedLevel || lvl.label === selectedLevel
  );
  const price = level?.price || 0;

  // Get suggested workshop level
  const suggestedLevel = useMemo(() => {
    // Always request the suggestion - the enhanced function will handle logging
    const suggestion = getSuggestedWorkshopLevel(age);
    console.log(`Suggesting workshop level ${suggestion || 'none'} for ${dancer.firstName} ${dancer.lastName} (age ${age}) - waiting for user selection`);
    return suggestion;
  }, [getSuggestedWorkshopLevel, age, dancer.firstName, dancer.lastName]);

  // Determine placeholder text based on whether there's a suggested level
  const placeholderText = useMemo(() => {
    if (suggestedLevel) {
      return `Suggested: ${suggestedLevel}`;
    } else if (workshopLevels.length === 1) {
      return `Suggested: ${workshopLevels[0].label}`;
    }
    return "Select Level";
  }, [suggestedLevel, workshopLevels]);

  // Handle level change
  const handleLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLevel = e.target.value;
    // Immediately update the local state for UI
    setLocalSelectedLevel(newLevel);
    
    // Get the dancer ID
    const dancerId = dancer.participantId || dancer.dancerId || dancer.originalId || '';
    
    // Then update in the backend - handle the promise properly
    try {
      const result = updateDancerOptions(dancerId, { workshopLevel: newLevel });
      
      // Check if result is a Promise and handle accordingly
      if (result && typeof result === 'object' && 'then' in result) {
        result.catch((error: Error) => {
          console.error("Error updating dancer workshop level:", error);
          // Optionally revert the local state if the update fails
          // setLocalSelectedLevel(initialLevel);
        });
      }
    } catch (error) {
      console.error("Error calling updateDancerOptions:", error);
    }
  };

  // Function to handle dancer removal
  const handleDancerToggle = () => {
    // Don't confirm if dancer is from a routine (should not be removable anyway)
    if (isFromRoutine) return;
    
    // Directly call toggleDancerSelection which will show the global confirmation dialog
    try {
      toggleDancerSelection(dancer);
    } catch (error) {
      console.error("Error calling toggleDancerSelection:", error);
    }
  };

  return (
    <div
      className={`border rounded-lg p-3 transition-all duration-200 ${
        !hasLevel
          ? isDarkMode
            ? "border-yellow-500/50 bg-yellow-500/10"
            : "border-yellow-300 bg-yellow-50"
          : isFromRoutine
          ? isDarkMode
            ? "border-blue-500/30 bg-blue-500/5"
            : "border-blue-300 bg-blue-50/80"
          : isDarkMode
          ? "border-primary/30 bg-primary/5"
          : "border-primary/20 bg-primary/5"
      }`}
    >
      {/* Top row with avatar, dancer info, and price */}
      <div className="flex items-start">
        {/* Avatar */}
        {dancer.profilePicture ? (
          <img
            src={dancer.profilePicture}
            alt={`${dancer.firstName} ${dancer.lastName}`}
            className={`w-10 h-10 rounded-full object-cover flex-shrink-0 ${
              !hasLevel
                ? isDarkMode
                  ? "border-2 border-yellow-500/30"
                  : "border-2 border-yellow-300"
                : isFromRoutine
                ? isDarkMode
                  ? "border-2 border-blue-500/30"
                  : "border-2 border-blue-300"
                : isDarkMode
                ? "border-2 border-primary/30"
                : "border-2 border-primary/20"
            }`}
          />
        ) : (
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              !hasLevel
                ? isDarkMode
                  ? "bg-yellow-500/20"
                  : "bg-yellow-100"
                : isFromRoutine
                ? isDarkMode
                  ? "bg-blue-500/20"
                  : "bg-blue-100"
                : isDarkMode
                ? "bg-primary/20"
                : "bg-primary/10"
            }`}
          >
            <span
              className={`font-medium text-sm ${
                !hasLevel
                  ? isDarkMode
                    ? "text-yellow-300"
                    : "text-yellow-600"
                  : isFromRoutine
                  ? isDarkMode
                    ? "text-blue-300"
                    : "text-blue-600"
                  : isDarkMode
                    ? "text-primary/80"
                    : "text-primary"
              }`}
            >
              {getInitials(dancer.firstName || '', dancer.lastName || '')}
            </span>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 min-w-0 mx-3">
          {/* Name row */}
          <div className="flex items-center mb-1">
            <h3 className="font-medium truncate text-sm">
              {dancer.firstName} {dancer.lastName}
            </h3>
            {/* Show routine source if applicable */}
            {isFromRoutine && (
              <span
                className={`ml-2 px-1.5 py-0.5 text-xs rounded-full ${
                  isDarkMode
                    ? "bg-blue-900/30 text-blue-300"
                    : "bg-blue-100 text-blue-600"
                } flex items-center`}
              >
                <Music className="h-3 w-3 mr-1 inline-block" />
                {hasMultipleRoutines ? (
                  <span className="flex items-center">
                    {primaryRoutine}
                    <span className="ml-1 w-4 h-4 bg-blue-500 rounded-full text-white flex items-center justify-center text-[10px]">
                      +{routines.length - 1}
                    </span>
                  </span>
                ) : (
                  primaryRoutine
                )}
              </span>
            )}
          </div>

          {/* Age */}
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Age: {age}
          </div>
        </div>

        {/* Price display and remove button */}
        <div className="flex-shrink-0 flex items-center gap-2 mt-1">
          <span
            className={`font-semibold ${
              !hasLevel
                ? isDarkMode
                  ? "text-gray-500"
                  : "text-gray-500"
                : isDarkMode
                ? "text-green-400"
                : "text-green-600"
            }`}
          >
            {formatCurrency(price)}
          </span>

          {/* X Button */}
          <button
            onClick={handleDancerToggle}
            className={`p-1 rounded-full ${
              isFromRoutine
                ? "text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/20 cursor-not-allowed relative group"
                : "text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
            }`}
            aria-label={
              isFromRoutine
                ? "Cannot remove dancer from routine"
                : "Remove dancer"
            }
            title={isFromRoutine ? "" : "Remove dancer"}
            disabled={isFromRoutine ? true : undefined}
          >
            <XCircle size={16} />

            {/* Tooltip for routine dancers */}
            {isFromRoutine && (
              <div className="absolute z-10 invisible group-hover:visible bg-gray-800 text-white text-xs rounded py-1 px-2 right-0 w-52 mt-1">
                {hasMultipleRoutines ? (
                  <>
                    To remove this dancer, you must first remove them from{" "}
                    <span className="font-bold text-blue-300">
                      all {routines.length} routines
                    </span>{" "}
                    or remove those routines from your registration.
                  </>
                ) : (
                  <>
                    To remove this dancer, you must first remove them from{" "}
                    <span className="font-bold text-blue-300">
                      {primaryRoutine}
                    </span>{" "}
                    or remove the routine from your registration.
                  </>
                )}
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Level Dropdown */}
      <div className="mt-3">
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
          Workshop Level
        </label>
        <select
          className={`select select-bordered select-sm w-full ${
            isDarkMode ? "bg-gray-700" : ""
          } ${
            !hasLevel
              ? isDarkMode
                ? "border-yellow-500/60"
                : "border-yellow-400"
              : ""
          }`}
          value={selectedLevel || ""}
          onChange={handleLevelChange}
          disabled={workshopLevels.length === 0}
        >
          <option value="" disabled>
            {placeholderText}
          </option>
          {workshopLevels.map((level) => {
            // Find corresponding age group info
            const ageGroupInfo = findAgeGroupInfo(level.label);
            // Highlight suggested level in the dropdown
            const isSuggested = level.label === suggestedLevel;
            return (
              <option 
                key={level.id || level.label} 
                value={level.label}
              >
                {level.label} {formatAgeRange(ageGroupInfo)}
                {isSuggested && !hasLevel ? " (Suggested)" : ""}
              </option>
            );
          })}
        </select>
        {!hasLevel && (
          <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
            {suggestedLevel 
              ? `Please select a level (${suggestedLevel} suggested for age ${age})` 
              : "Level selection required"}
          </p>
        )}
        {workshopLevels.length === 0 && (
          <p className="text-xs text-warning mt-1">
            No workshop levels defined
          </p>
        )}
      </div>
    </div>
  );
};

export default SelectedDancerCard;